import { useMemo } from 'react'
import { PiArrowDown, PiArrowUp, PiCheck, PiX } from 'react-icons/pi'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import { chartTheme } from '../widgets/chart-theme'
import { cn } from 'components/UI/cn'
import { metricSeries } from '../engine'
import { formatMeasure } from '../format'
import { frameOf } from '../frames'
import { goalMet } from '../kpis'
import { QueryResult, ViewConfig } from '../view-config'

/**
 * Metric view of a report — a stat-card KPI. The number is the point; optional
 * extras add context (config-driven, all ignored when absent):
 *   - threshold: a met/missed target badge beside the number
 *   - trend: a period-over-period delta badge + the prior period's value
 *   - sparkline: the measure over recent periods
 * Designed to drop cleanly into a dashboard tile when pinned.
 */
export function MetricView({
  config,
  result,
  fill = false,
}: {
  config: ViewConfig
  result: QueryResult
  /**
   * Fill the parent's height and scale the headline number to it (for a
   * dashboard tile, whose height the grid controls). The number sizes via CSS
   * container-query units, so resizing the tile grows/shrinks it with no dead
   * gap. Off (detail page) keeps a fixed, sensibly-sized number.
   */
  fill?: boolean
}) {
  const granularity = config.trend ?? 'month'
  const series = useMemo(
    () =>
      config.trend || config.sparkline
        ? metricSeries(config, frameOf(config.frame), granularity)
        : [],
    [config, granularity]
  )

  if (result.kind !== 'aggregate') return null
  const label = config.chartMetric ?? config.metrics?.[0]?.label ?? ''
  const format = (n: number) => formatMeasure(label, n, true)

  // Period-over-period trend (last complete bucket vs the prior one).
  const current = series[series.length - 1]?.value
  const previous = series[series.length - 2]?.value

  // With a period selected, the headline IS that latest bucket; the badge below
  // compares it to the prior one. No period ("All time") = aggregate of all rows.
  const value =
    config.trend && current !== undefined
      ? current
      : (result.total.metrics[label] ?? 0)
  const threshold = config.threshold
  const met = goalMet(value, threshold)

  const deltaPct =
    config.trend && previous !== undefined && previous !== 0
      ? ((current - previous) / previous) * 100
      : null
  // Name the two buckets being compared so it's not a mystery window.
  // Month keys ("2026-06") get a friendly label; quarter/year keys are already readable.
  const periodLabel = (key: string) =>
    /^\d{4}-\d{2}$/.test(key)
      ? new Date(`${key}-01`).toLocaleString('en-US', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : key
  const currentPeriod = series[series.length - 1]?.period
  const previousPeriod = series[series.length - 2]?.period

  const gradientId = `metric-spark-${config.id}`

  return (
    <div
      className={cn(
        'flex flex-col gap-1',
        // Center in the tile and become a size container so the number's cqh
        // units resolve against the tile height.
        fill && 'h-full justify-center [container-type:size]'
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <span
          className={cn('font-bold tabular-nums text-fg', !fill && 'text-3xl')}
          // Dynamic, tile-relative size (allowed inline per client/CLAUDE.md);
          // clamp keeps it legible in tiny tiles and capped in huge ones.
          style={fill ? { fontSize: 'clamp(1.5rem, 18cqh, 4rem)' } : undefined}
        >
          {format(value)}
        </span>
        {threshold !== undefined && (
          <span
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold',
              met ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
            )}
          >
            {/* Icon + color (not color alone) so met/missed is distinguishable
                for red/green colorblind viewers. */}
            {met ? <PiCheck size={16} /> : <PiX size={16} />}
            {threshold.dir === 'above' ? '≥' : '≤'} {format(threshold.value)}
          </span>
        )}
      </div>

      {/* Trend vs previous period */}
      {deltaPct !== null && (
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-semibold',
              deltaPct >= 0
                ? 'bg-success/10 text-success'
                : 'bg-error/10 text-error'
            )}
          >
            {deltaPct >= 0 ? (
              <PiArrowUp size={16} />
            ) : (
              <PiArrowDown size={16} />
            )}
            {Math.abs(deltaPct).toFixed(1)}%
          </span>
          <span className="text-sm text-fg-secondary">
            {periodLabel(currentPeriod!)} vs {periodLabel(previousPeriod!)} (
            {format(previous!)})
          </span>
        </div>
      )}

      {/* Sparkline */}
      {config.sparkline && series.length >= 2 && (
        <div className="h-10 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={series}
              margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={chartTheme.primary}
                    stopOpacity={0.5}
                  />
                  <stop
                    offset="95%"
                    stopColor={chartTheme.primary}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={chartTheme.primary}
                strokeWidth={1.5}
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
